import React, { useState, useEffect } from 'react'

import { Typography, Button, Box } from '@material-ui/core'
import { createStyles, makeStyles } from '@material-ui/core/styles'

import { llamayaLink } from '../ui/theme/colors'
import { fonts } from '../ui/theme/fonts'
import { CustomTheme } from '../ui/theme/utils'

interface Button {
  label?: string
  icon: React.ReactNode
  iconSelected?: React.ReactNode
  value: string
  disabled: boolean
}

interface ButtonSelectorProps {
  title: string
  buttons: Button[]
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSelect: (value: any) => void
  preSelected?: string
  wideButtons?: boolean
  shouldReset?: boolean
}

const useStyles = makeStyles(({ palette, name, spacing }: CustomTheme) =>
  createStyles({
    btn: {
      transition: 'all 0.2s ease-in',
      '& .icon-wrapper > *': {
        fontSize: fonts.size.large,
        marginTop: spacing(1),
      },
      marginRight: spacing(6),
      borderRadius: '8px',
      border: `solid 1px ${palette.grey[200]}`,
      textTransform: 'none',
      fontWeight: 'normal',
      fontSize: fonts.size.small,
      padding: `${spacing(2)}px ${spacing(4)}px ${spacing(2)}px ${spacing(3)}px`,
      height: '32px',
      maxWidth: '280px',
      boxShadow: `0 1px 3px 0 rgba(0, 0, 0, 0.2), 0 2px 1px -1px rgba(0, 0, 0, 0.12), 0 1px 1px 0 rgba(0, 0, 0, 0.14);`,
      color: palette.grey[600],
      '&:hover': {
        border: `solid 1px ${name === 'masmovil' ? palette.primary.dark : llamayaLink.main}`,
        backgroundColor: `${name === 'masmovil' ? palette.primary.light : '#F0D3F5'}`,
        color: palette.grey[900],
      },
      '&.wide': {
        padding: spacing(3),
        width: '280px',
        marginRight: spacing(4),
        height: '48px',
      },
      '&.wide:last-of-type': {
        marginRight: 0,
      },
      '&.selected': {
        color: palette.grey[900],
        backgroundColor: `${name === 'masmovil' ? palette.primary.light : '#F0D3F5'}`,
        border: `solid 1px ${name === 'masmovil' ? palette.primary.dark : llamayaLink.main}`,
      },
      '&.wide.selected': {
        backgroundColor: palette.primary.main,
      },
      '&.Mui-disabled': {
        boxShadow:
          '0 1px 3px 0 rgba(0, 0, 0, 0.2), 0 2px 1px -1px rgba(0, 0, 0, 0.12), 0 1px 1px 0 rgba(0, 0, 0, 0.14);',
        backgroundColor: `${name === 'masmovil' ? palette.grey[200] : '#FAFAFA'}`,
        border: 'none',
        color: `${name === 'masmovil' ? '#b6b6b6' : '#AEAEAE'}`,
      },
    },
  }),
)

export default function ButtonSelector({
  title,
  buttons,
  onSelect,
  preSelected,
  wideButtons,
  shouldReset,
}: ButtonSelectorProps) {
  const { btn } = useStyles()
  const [selected, setSelected] = useState(preSelected)

  function onClickButton(value: string) {
    onSelect(value)
    setSelected(value)
  }

  useEffect(() => {
    setSelected(preSelected)
  }, [preSelected])

  useEffect(() => {
    if (shouldReset) setSelected('')
  }, [shouldReset])

  return (
    <Box>
      <Typography variant="body1">{title}</Typography>
      <Box mt="20px">
        {buttons.map(({ value, disabled, icon, label, iconSelected }) => (
          <Button
            key={value}
            disabled={disabled}
            variant="outlined"
            color="secondary"
            value={value}
            data-hook={`button-${value}`}
            onClick={() => onClickButton(value)}
            className={`${btn} ${selected === value ? 'selected' : ''} ${wideButtons && 'wide'}`}
            startIcon={iconSelected && selected === value ? iconSelected : icon}
          >
            {label}
          </Button>
        ))}
      </Box>
    </Box>
  )
}

ButtonSelector.defaultProps = {
  onSelect: () => null,
}
